// src/api/base.ts
//
// Shared HTTP client factory used by all API clients.
// Handles auth header injection, response parsing, and error formatting.

import { getAuthHeaders } from '../utils/sessionAuth'

export class ApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT'
  /** JSON-serialisable body. Mutually exclusive with rawBody. */
  body?: unknown
  /** Pre-built body (e.g. FormData). Skips JSON serialisation and Content-Type injection. */
  rawBody?: BodyInit
  signal?: AbortSignal
  /** Extra headers merged on top of the auth headers. */
  headers?: Record<string, string>
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return null
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/**
 * Creates an HTTP client bound to the URL returned by `getBaseUrl`.
 *
 * Usage:
 * ```ts
 * const client = createApiClient(getCrmServiceUrl)
 * const data = await client.request<MyType>('/some/path', { method: 'POST', body: payload })
 * ```
 */
export function createApiClient(getBaseUrl: () => string) {
  async function request<T = unknown>(path: string, options?: RequestOptions): Promise<T> {
    const { method = 'GET', body, rawBody, signal, headers: extraHeaders = {} } = options ?? {}

    const hasJsonBody = body !== undefined
    const response = await fetch(`${getBaseUrl()}${path}`, {
      method,
      headers: {
        ...getAuthHeaders(),
        ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
        ...extraHeaders,
      },
      credentials: 'include',
      body: rawBody ?? (hasJsonBody ? JSON.stringify(body) : undefined),
      signal,
    })

    const data = await parseResponse(response)
    if (!response.ok) {
      const message =
        typeof data === 'string' ? data : data ? JSON.stringify(data) : response.statusText
      throw new ApiError(
        response.status,
        `${response.status} ${response.statusText}${message ? `: ${message}` : ''}`,
      )
    }
    return data as T
  }

  return { request }
}

export async function refreshAccessToken(): Promise<string | null> {
  return null
}
